import { apiTokenJwtUpdate, apiTokens } from "@smp/api";
import { dataArraySieve, REACT_APP__URL_GRAPHQL } from "@smp/utils";
import { GraphQLClient } from "graphql-request";

const abortControllers: Map<string, AbortController> = new Map();

const getKey = (body: RequestInit["body"]) => {
  if (typeof body !== "string") {
    return null;
  }

  try {
    const bodyParsed = JSON.parse(body);
    const keyArray = dataArraySieve([
      bodyParsed.operationName,
      bodyParsed.variables,
    ]);

    if (!keyArray.length) {
      throw new Error();
    }

    return JSON.stringify(keyArray);
  } catch (e) {
    return null;
  }
};

/**
 * Custom fetch fucntion which is used to update token on 403
 * and refetch query
 */
const fetcher = async (
  url: string,
  options: RequestInit,
  isRefetch: boolean = false,
) => {
  const key = getKey(options.body);
  let signal: AbortSignal | null = null;

  if (key) {
    const controller = new AbortController();
    abortControllers.set(key, controller);
    signal = controller.signal;
  }

  const headers = new Headers(options.headers);
  const token = apiTokens.smp;
  const xTimestamp = Math.floor(Date.now() / 1000);
  const xTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const ep = JSON.parse(localStorage.getItem("ep") || "{}");
  const organizationId = ep.organization_id || null;
  const language = ep.language_code || "en";
  const acceptLanguage =
    language === "en"
      ? "en-US, en;q=0.9, *;q=0.5"
      : "ru-RU, ru;q=0.9, en;q=0.8, *;q=0.5";

  headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Timestamp", `${xTimestamp}`);
  headers.set("X-Timezone", `${xTimezone}`);
  headers.set("Accept-Language", `${acceptLanguage}`);

  if (!headers.has("x-organization-context") && organizationId) {
    headers.set("x-organization-context", `${organizationId}`);
  }

  let res = await fetch(url, { ...options, signal, headers }).finally(() => {
    if (key) {
      abortControllers.delete(key);
    }
  });

  if (res.status === 403 && !isRefetch) {
    await apiTokenJwtUpdate();
    res = await fetcher(url, options, true);
  }

  return res;
};

export const abortGqlRequest = (key: string | Array<any>) => {
  try {
    const keyString = typeof key === "string" ? key : JSON.stringify(key);
    const controller = abortControllers.get(keyString);

    if (controller) {
      controller.abort();
    }
  } catch (e) {}
};

export const gqlClient = new GraphQLClient(REACT_APP__URL_GRAPHQL, {
  fetch: fetcher,
});
